import { useEffect } from 'react';
import { generatePath, Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { Box, Breadcrumbs, Card, CardActions, CardContent, Link, Table } from '@mui/material';
import _ from 'lodash';

import { usePrevious } from 'common/hooks';
import { breadcrumbIconSx, breadcrumbLinkSx, breadcrumbsContainerSx, breadcrumbsSx } from 'common/styles';

import { DetailsCardTitle } from 'commonComponents/DetailsCardTitle';
import { LinkButton } from 'commonComponents/LinkButton';
import { LoadingIndicator } from 'commonComponents/LoadingIndicator';
import { PageContainer } from 'commonComponents/PageContainer';

import { useProductConfiguration } from 'api/productConfigurations';
import { Product, useCurrentProduct } from 'api/products';
import { EditIcon, UndoIcon } from 'icons';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { RoleDependentComponent } from 'roles/RoleDependentComponent';
import { ROUTES } from 'routes/routes';

import { ConfigurationDetailsRows } from './ConfigurationDetailsRow';
import { DeleteOrInvalidateConfiguration } from './DeleteOrInvalidateConfiguration';

export function ConfigurationDetails() {
    const { id: definitionId } = useParams<{ id: string }>();
    const { data: currentProduct } = useCurrentProduct();
    const previousProduct: Product = usePrevious(currentProduct);
    const navigate = useNavigate();

    const { data: configuration, isLoading: isFetching } = useProductConfiguration(Number(definitionId));

    useEffect(() => {
        if (previousProduct && !_.isEqual(previousProduct, currentProduct)) {
            navigate(ROUTES.CONFIGURATION_LIST);
        }
    }, [currentProduct, previousProduct, navigate]);

    return (
        <PageContainer
            maxWidth="lg"
            sx={{
                '& .MuiContainer-root': {
                    paddingTop: 0,
                },
            }}
        >
            <Box sx={breadcrumbsContainerSx}>
                <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbsSx}>
                    <Link
                        component={RouterLink}
                        to={ROUTES.CONFIGURATION_LIST}
                        underline="hover"
                        data-testid="ConfigurationDetails_BreadcrumbConfigurations"
                        sx={breadcrumbLinkSx}
                    >
                        <UndoIcon sx={breadcrumbIconSx} />
                        Product Configurations
                    </Link>
                </Breadcrumbs>
            </Box>
            {isFetching || !configuration ? (
                <LoadingIndicator />
            ) : (
                <Card>
                    <CardContent data-testid="ConfigurationDetails_CardContent">
                        <DetailsCardTitle
                            subtitle={`Configuration${configuration.internal ? ' [Only for internal use]' : ''} ${configuration.invalidated ? '[INVALIDATED]' : ''}`}
                            title={configuration.name}
                            showError={configuration.internal || configuration.invalidated}
                        />
                        <Table size="small">
                            <ConfigurationDetailsRows configuration={configuration} />
                        </Table>
                    </CardContent>
                    <CardActions>
                        {!configuration.invalidated && (
                            <>
                                <RoleDependentComponent
                                    userRole={ROLE_DEFINITIONS.LicenseManager}
                                    component={LinkButton}
                                    iconClass={EditIcon}
                                    route={generatePath(ROUTES.CONFIGURATION_EDIT, { id: definitionId })}
                                    title="Edit"
                                    data-testid="EditConfig_Button"
                                />
                                <DeleteOrInvalidateConfiguration configuration={configuration} />
                            </>
                        )}
                    </CardActions>
                </Card>
            )}
        </PageContainer>
    );
}
